// ============================================================
// EffectiveProfileCard - Unified effective weapon profile at
// the top of the Controls cockpit (v8.229.0). Merges the former
// Overview combat-stats card and WeaponProfileCard: collapsed
// chip row with the numbers an attack will actually use (via
// attackCalc), and an expanded detail view with character
// stats, talents and special-rule descriptions. Carries the
// slot-editor pencil in its header - it replaced the cockpit's
// separate weapon strip.
// ============================================================
import { memo, useState, type ReactNode } from 'react';
import styles from '../styles/EffectiveProfileCard.module.css';
import clsx from '../utils/clsx';
import { DAMAGE_TYPE_COLORS } from '../engine/constants';
import { fmtWS, parseWS, wsLabel, wsDescription, hasWS } from '../engine/weaponSpecials';
import { calcModifiedSkill, calcEffectivePen } from '../engine/attackCalc';
import type { Slot } from '../types';
import type { AttackerCombatStats } from '../engine/character/types';
import { onActivateKey } from '../utils/keyActivation';
import {
  attackModes,
  damageBonus,
  damageSuffix,
  formatRoF,
  psyRatingOf,
  rerollTalents,
  type RerollTalent,
} from './effectiveProfile';

interface EffectiveProfileCardProps {
  slot: Slot | null;
  isMelee: boolean;
  attackMod: number;
  targetSize: string;
  rangeBand: string;
  hatred: boolean;
  frenzied: boolean;
  allOutAttack: boolean;
  attackerCharStats: AttackerCombatStats | null;
  /** True when weapon overrides are active - slot is the overridden view */
  modified?: boolean;
  /**
   * True while an aim is prepared (Ah/Af). Feeds Accurate's extra +10
   * so the Eff skill chip agrees with the modifier ledger - omitting
   * it was the v8.229.0 drift.
   */
  aimed?: boolean;
  /** Renders the edit pencil in the header when provided. */
  onEdit?: (() => void) | null;
}

/** What the card shows for this attack, computed once. */
interface Profile {
  slot: Slot;
  isMelee: boolean;
  effectiveSkill: number;
  pen: number;
  dmgBonus: number;
  dmgColor: { bg: string; label: string };
  specials: string[];
}

function EffectiveProfileCard(props: EffectiveProfileCardProps) {
  const { slot, isMelee, attackerCharStats, modified, onEdit = null } = props;
  const [expanded, setExpanded] = useState(false);

  if (!slot || slot.isEmpty) {
    return <div className={styles.empty}>Select a weapon from the Armory to see its effective profile.</div>;
  }
  const specials = slot.specialRules || [];
  const profile: Profile = {
    slot,
    isMelee,
    effectiveSkill: effectiveSkillOf(props, slot),
    pen: calcEffectivePen(slot.penetration, specials, 0, props.rangeBand).pen,
    dmgBonus: damageBonus(attackerCharStats, isMelee),
    dmgColor: DAMAGE_TYPE_COLORS[slot.damageType] || DAMAGE_TYPE_COLORS['I'],
    specials,
  };
  const toggle = () => setExpanded((e) => !e);
  return (
    <div className={styles.card} data-testid="effective-profile-card">
      <ProfileHeader
        slot={slot}
        isMelee={isMelee}
        expanded={expanded}
        modified={modified}
        onToggle={toggle}
        onEdit={onEdit}
      />
      <CompactChips p={profile} />
      {/* ── Special rules tags (always visible) ── */}
      {specials.length > 0 && (
        <div className={styles.specialsRow}>
          {specials.map((sr, i) => (
            <span key={i} className={styles.specialTag} title={wsDescription(parseWS(sr).id)}>
              {fmtWS(sr)}
            </span>
          ))}
        </div>
      )}
      {/* Hatred/Frenzied banners died in v8.229.0: the switch bank's lamps
          and the ledger's chips carry that state now. */}
      {expanded && (
        <>
          <DetailGrid p={profile} stats={attackerCharStats} />
          <RerollPills rerolls={rerollTalents(attackerCharStats, isMelee, props.allOutAttack)} />
          <SpecialDescriptions specials={specials} />
        </>
      )}
    </div>
  );
}

/** The skill an attack will actually use (attackCalc), aim included for Accurate. */
function effectiveSkillOf(props: EffectiveProfileCardProps, slot: Slot): number {
  const stats = props.attackerCharStats;
  return calcModifiedSkill(
    slot,
    props.attackMod,
    props.targetSize,
    props.rangeBand,
    props.isMelee,
    props.hatred,
    props.frenzied,
    stats?.removeLongRangePenalty ?? false,
    stats?.removeExtremeRangePenalty ?? false,
    props.aimed ?? false,
  );
}

interface HeaderProps {
  slot: Slot;
  isMelee: boolean;
  expanded: boolean;
  modified?: boolean;
  onToggle: () => void;
  onEdit: (() => void) | null;
}

/** The header toggles the detail; it carries the slot-editor pencil. */
function ProfileHeader({ slot, isMelee, expanded, modified, onToggle, onEdit }: HeaderProps) {
  return (
    <div
      className={styles.header}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={onActivateKey(onToggle)}
    >
      <span className={styles.typeIcon}>{isMelee ? '⚔' : '🎯'}</span>
      <span className={styles.weaponName}>{slot.weaponName}</span>
      {modified && (
        <span className={styles.modifiedBadge} title="Weapon overrides active - see Attack Overrides above">
          Modified
        </span>
      )}
      <span className={styles.headerHint}>Effective Profile</span>
      {onEdit && (
        <button
          type="button"
          className={styles.pencil}
          aria-label={`Edit ${slot.weaponName}`}
          title="Edit this weapon slot"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          // The header div handles Enter/Space itself - a keypress on
          // the pencil must not also toggle the expansion.
          onKeyDown={(e) => e.stopPropagation()}
        >
          ✎
        </button>
      )}
      <span className={clsx(styles.expandArrow, expanded && styles.expandArrowOpen)}>&#9654;</span>
    </div>
  );
}

/** The numbers an attack will use, always visible. */
function CompactChips({ p }: { p: Profile }) {
  const { slot, isMelee, effectiveSkill } = p;
  return (
    <div className={styles.compactRow}>
      <span className={clsx(styles.chip, styles.chipAccent)}>
        <span className={styles.chipLabel}>{isMelee ? 'Eff WS' : 'Eff BS'}</span>
        <span className={styles.chipValue}>{effectiveSkill}</span>
        {effectiveSkill !== slot.skillValue && <span className={styles.chipNote}>({slot.skillValue})</span>}
      </span>
      <span className={styles.chip}>
        <span className={styles.chipLabel}>Dmg</span>
        <span className={styles.chipValue}>
          <span className={styles.dmgDot} style={{ background: p.dmgColor.bg }} />
          {slot.damageExpression}
          {damageSuffix(p.dmgBonus)}
        </span>
      </span>
      <Chip label="Pen" value={p.pen} />
      {isMelee ? <Chip label="SB" value={`+${slot.strengthBonus}`} /> : <Chip label="RoF" value={formatRoF(slot)} />}
      {slot.righteousFury && (
        <span className={styles.chip} style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}>
          <span className={styles.chipValue}>RF</span>
        </span>
      )}
    </div>
  );
}

function Chip({ label, value }: { label: string; value: string | number }) {
  return (
    <span className={styles.chip}>
      <span className={styles.chipLabel}>{label}</span>
      <span className={styles.chipValue}>{value}</span>
    </span>
  );
}

/** The expanded detail: the effective numbers and where they come from. */
function DetailGrid({ p, stats }: { p: Profile; stats: AttackerCombatStats | null }) {
  const { slot, isMelee, effectiveSkill } = p;
  const psy = psyRatingOf(stats, slot);
  return (
    <div className={styles.detailGrid}>
      <span className={styles.detailLabel}>{isMelee ? 'Effective WS' : 'Effective BS'}</span>
      <span className={styles.detailValue}>
        {effectiveSkill}
        {effectiveSkill !== slot.skillValue && <small className={styles.baseNote}> (base {slot.skillValue})</small>}
      </span>
      <span className={styles.detailLabel}>Damage</span>
      <span className={styles.detailValue}>
        <span className={styles.dmgDot} style={{ background: p.dmgColor.bg }} />
        {slot.damageExpression}
        {damageSuffix(p.dmgBonus, true)} {p.dmgColor.label}
      </span>
      <span className={styles.detailLabel}>Penetration</span>
      <span className={styles.detailValue}>
        {p.pen}
        {hasWS(p.specials, 'Mono') ? ' (incl. Mono +2)' : ''}
        {hasWS(p.specials, 'RazorSharp') ? ' (×2 at 2+ DoS)' : ''}
      </span>
      {stats && <Detail label={isMelee ? 'Melee Dmg' : 'Ranged Dmg'}>+{p.dmgBonus}</Detail>}
      {isMelee ? <MeleeDetail slot={slot} /> : <RangedDetail slot={slot} />}
      <Detail label="Righteous Fury">{slot.righteousFury ? 'Yes' : 'No'}</Detail>
      {psy > 0 && <Detail label="Psy Rating">{psy}</Detail>}
    </div>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{children}</span>
    </>
  );
}

function MeleeDetail({ slot }: { slot: Slot }) {
  return (
    <>
      <Detail label="Str Bonus">+{slot.strengthBonus}</Detail>
      <Detail label="Attack Modes">{attackModes(slot).join(' / ')}</Detail>
    </>
  );
}

function RangedDetail({ slot }: { slot: Slot }) {
  return (
    <>
      <Detail label="Range">{slot.range}m</Detail>
      <Detail label="Rate of Fire">{formatRoF(slot)}</Detail>
      <Detail label="Clip">
        {slot.ammo.current}/{slot.ammo.max} ({slot.ammo.reloadAction} reload)
      </Detail>
    </>
  );
}

/** Re-roll talent pills. */
function RerollPills({ rerolls }: { rerolls: RerollTalent[] }) {
  if (rerolls.length === 0) return null;
  return (
    <div className={styles.rerollRow}>
      {rerolls.map((r) => (
        <span key={r.label} className={styles.rerollPill} style={{ borderColor: r.color, color: r.color }}>
          {r.label}
        </span>
      ))}
    </div>
  );
}

/** Special rules with descriptions. */
function SpecialDescriptions({ specials }: { specials: string[] }) {
  if (specials.length === 0) return null;
  return (
    <div className={styles.specialDesc}>
      {specials.map((sr, i) => {
        const parsed = parseWS(sr);
        return (
          <div key={i} className={styles.specialDescItem}>
            <span className={styles.specialDescName}>
              {wsLabel(parsed.id)}
              {parsed.p > 0 ? `(${parsed.p})` : ''}
            </span>
            {wsDescription(parsed.id)}
          </div>
        );
      })}
    </div>
  );
}

export default memo(EffectiveProfileCard);
